<script lang="ts">
import { Button } from "@pre6ai/lens-ui/ui/button";
import { onMount } from "svelte";
import ArrowRight from "lucide-svelte/icons/arrow-right";
import Calculator from "lucide-svelte/icons/calculator";
import Check from "lucide-svelte/icons/check";
import Plus from "lucide-svelte/icons/plus";
import BadgeCheck from "lucide-svelte/icons/badge-check";
import CalendarClock from "lucide-svelte/icons/calendar-clock";
import FileCheck from "lucide-svelte/icons/file-check";
import FileDiff from "lucide-svelte/icons/file-diff";
import Rocket from "lucide-svelte/icons/rocket";
import SearchCheck from "lucide-svelte/icons/search-check";
import TrendingDown from "lucide-svelte/icons/trending-down";
import Truck from "lucide-svelte/icons/truck";
import Cloud from "lucide-svelte/icons/cloud";
import Crosshair from "lucide-svelte/icons/crosshair";
import Mail from "lucide-svelte/icons/mail";
import Server from "lucide-svelte/icons/server";
import ShieldCheck from "lucide-svelte/icons/shield-check";
import Hero from "./enterprise-hero.svelte";
import HeroStory from "./hero-story.svelte";
import SectorStory from "./sector-story.svelte";
import RotatingLede from "./rotating-lede.svelte";
import CompareEcn from "./compare-ecn.svelte";
import SpeedStrip from "./speed-strip.svelte";
import Highlight from "$lib/components/home/highlight.svelte";
import SectionHeading from "$lib/components/home/section-heading.svelte";
import Security from "$lib/components/home/security.svelte";
import { COUNTRIES, DEFAULT_COUNTRY, type Country } from "$lib/data/countries";
import {
	MAX_COMPANY_LENGTH,
	MAX_EMAIL_LENGTH,
	normalizeCompany,
	readUtmParams,
	validateCompany,
	validateEmail,
	validatePhone,
} from "$lib/utils/lead-validation";

/**
 * /enterprise content in the home page's visual system: same layout, nav, backdrop and drafting
 * grid (from the (home) group), section headings with the red stop, and bordered sheets split
 * into zones under a ruler, like the home page's Security section.
 */

const EMAIL = "hello@pre6.ai";

// The results graphics play once, when they scroll into view.
onMount(() => {
	const io = new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				if (!e.isIntersecting) continue;
				e.target.setAttribute("data-seen", "");
				io.unobserve(e.target);
			}
		},
		{ threshold: 0.3 },
	);
	for (const el of document.querySelectorAll(".ens-light .proof")) io.observe(el);
	return () => io.disconnect();
});

/**
 * Agents a team could ask for beyond the three this page follows, each owning a big part of a job,
 * in the agents' colors. They drift past in one row; the card at the end is the reader's own.
 */
const AGENT_HUES = ["var(--agent-pre6)", "var(--agent-rfq)", "var(--agent-prod)", "var(--agent-qa)"];
const COULD_HAVE = [
	{ name: "New product introduction", icon: Rocket },
	{ name: "Supplier management", icon: Truck },
	{ name: "Production scheduling", icon: CalendarClock },
	{ name: "8D investigations", icon: SearchCheck },
	{ name: "APQP and PPAP", icon: FileCheck },
	{ name: "Engineering change rollout", icon: FileDiff },
	{ name: "Audit readiness", icon: BadgeCheck },
	{ name: "Cost reduction", icon: TrendingDown },
].map((a, i) => ({ ...a, color: AGENT_HUES[i % AGENT_HUES.length] }));

/**
 * The results sheet's graphics, drawn like the sector sheets: ink line work, with what the agents add
 * in their colors (the same as the hero's RFQ and Quality agents). Each flag pairs a feature on the
 * drawing with the same one on the model.
 */
const RFQ_COLOR = "var(--agent-rfq)";
const QA_COLOR = "var(--agent-qa)";
const CHECK_FLAGS = [
	{ label: "Missing dimension", short: "Dimension", a: [176, 62], b: [432, 78], r: 11, link: "M187 62L212 38H418L430 72" },
	{ label: "Conflicting tolerance", short: "Tolerance", a: [114, 92], b: [380, 78], r: 30, link: "M136 92L356 80" },
	{ label: "Datum mismatch", short: "Datum", a: [34, 120], b: [300, 100], r: 11, link: "M34 131L58 154H246L300 100" },
];

const systems = ["Your PLM", "Your ERP", "Your MES", "Your QMS", "Email inbox", "Customer and supplier portals", "Custom internal tools", "Shop-floor stations"];

const deployments = [
	{ icon: Cloud, title: "Pre6 Cloud", lead: "We host and run everything,", body: "so your team has nothing to maintain." },
	{ icon: Server, title: "Pre6 Edge", lead: "Your data stays on your premises.", body: "" },
	{ icon: ShieldCheck, title: "On-prem", lead: "Fully air-gapped,", body: "with everything under your control." },
];

// Contact form: posts to the lead API, tagged as an enterprise lead.
type FormState = "idle" | "submitting" | "success" | "error";
let formState: FormState = $state("idle");
let email = $state("");
let company = $state("");
let country: Country = $state(DEFAULT_COUNTRY);
let phone = $state("");
let touched = $state({ email: false, company: false, phone: false });
const fullPhone = $derived(`${country.dial} ${phone}`.trim());
const issues = $derived({ email: validateEmail(email), company: validateCompany(company), phone: validatePhone(fullPhone) });
const isValid = $derived(!issues.email && !issues.company && !issues.phone);

function handlePhoneInput(e: Event) {
	if (!(e.target instanceof HTMLInputElement)) return;
	e.target.value = e.target.value.replace(/[^0-9 ]/g, "").slice(0, 12);
	phone = e.target.value;
}

async function handleSubmit() {
	touched = { email: true, company: true, phone: true };
	if (!isValid || formState === "submitting") return;
	formState = "submitting";
	const response = await fetch("/api/contact-lead", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			email: email.trim(),
			company: normalizeCompany(company),
			phone: fullPhone,
			page: "enterprise",
			...readUtmParams(window.location.search),
		}),
	}).catch(() => null);
	formState = response?.ok ? "success" : "error";
}
</script>

<svelte:head>
	<title>Pre6 Enterprise · AI agents for manufacturers</title>
	<meta
		name="description"
		content="Pre6 builds AI agents tuned on your quotes, drawings and ECNs, wired into your PLM, ERP and QMS, and deployed in the cloud, at the edge or on-prem."
	/>
</svelte:head>

{#snippet couldHave()}
	<div class="could">
		<span class="label">Agents you could have</span>
		<div class="could-body">
			<div class="lanes">
				<div class="lane">
					<!-- Two copies, so the row loops seamlessly; only the first is read out. -->
					{#each [0, 1] as copy (copy)}
						<ul class="lane-run" aria-hidden={copy === 1 || undefined}>
							{#each COULD_HAVE as a (a.name)}
								<li class="ag-card" style:--c={a.color}>
									<span class="ag-ic"><a.icon size={15} strokeWidth={2.25} /></span>
									<span class="ag-t"><b>{a.name}</b></span>
								</li>
							{/each}
						</ul>
					{/each}
				</div>
			</div>
			<a class="yours" href="#get-in-touch">
				<span class="yours-ic"><Plus size={18} strokeWidth={2.5} /></span>
				<span class="yours-t"><b>Your agent</b></span>
			</a>
		</div>
	</div>
{/snippet}

{#snippet results()}
	<div class="proof sheet" id="stories" data-drafting="results">
		<div class="head">
			<SectionHeading id="results-title" title="Agents in production">
				Two of the agents we've built, measured against the targets each customer set <Highlight>before we started</Highlight>.
			</SectionHeading>
		</div>
		<div class="scale" aria-hidden="true"></div>
		<ul class="zones cols-2">
			<li class="zone">
				<!-- 504 hours of quoting (three weeks) against 24: two plain bars on one scale, drawn in once. -->
				<div class="viz turn" role="img" aria-label="Hours to quote one RFQ: 504 before, 24 with Pre6" style:--ag={RFQ_COLOR}>
					<span class="v-k">Hours to quote one RFQ</span>
					<span class="v-agent"><span class="v-av"><Calculator size={11} strokeWidth={2.25} /></span>RFQ Agent</span>
					<div class="bars">
						<span class="b-k">Before</span>
						<span class="b-track"><i class="b-slow"></i></span>
						<b class="b-v">504 h</b>
						<span class="b-k strong">With Pre6</span>
						<span class="b-track"><i class="b-fast"></i></span>
						<b class="b-v fast">24 h</b>
						<span class="gain"><b>480 hours</b> back to your experts</span>
					</div>
				</div>
				<span class="label red">Quote turnaround</span>
				<p class="metric"><s>3 weeks</s> <strong>24 hours</strong></p>
				<h3>Faster quotes, with every estimate worked up from routing and cycle times</h3>
				<p>Estimators <b>review and approve</b> cost estimates instead of building costing workbooks, and every RFQ comes with <b>similar past parts attached</b>.</p>
				<p class="who">For a Tier-1 auto components manufacturer <span class="chip">RFQ Agent</span></p>
			</li>
			<li class="zone">
				<!-- The drawing and its model in ink: each issue is ringed on the drawing and matched on the model. -->
				<div class="viz check" aria-hidden="true" style:--ag={QA_COLOR}>
					<span class="v-k">2D drawing vs 3D model</span>
					<span class="v-agent"><span class="v-av"><Crosshair size={11} strokeWidth={2.25} /></span>Quality Agent</span>
					<svg viewBox="0 6 480 166">
						<defs>
							<marker id="rv-arr" viewBox="0 0 8 8" refX="7.5" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
								<path class="arrowhead" d="M0 1.2L8 4L0 6.8Z" />
							</marker>
							<pattern id="rv-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
								<path class="hatchline" d="M0 0V5" />
							</pattern>
						</defs>
						<path class="paper" d="M8 22H232V162H8Z" />
						<path class="thin" d="M178 148H232M178 148V162M205 148V162" />
						<path class="ink" pathLength="1" d="M44 44H184A10 10 0 0 1 194 54V130A10 10 0 0 1 184 140H44A10 10 0 0 1 34 130V54A10 10 0 0 1 44 44Z" />
						<path class="ink" pathLength="1" style:--d="0.25" d="M92 92a22 22 0 1 0 44 0a22 22 0 1 0 -44 0" />
						<path class="ink" pathLength="1" style:--d="0.35" d="M99 92a15 15 0 1 0 30 0a15 15 0 1 0 -30 0" />
						{#each [[52, 62], [176, 62], [52, 122], [176, 122]] as [x, y], i (x + "-" + y)}<path class="ink" pathLength="1" style:--d={0.4 + i * 0.05} d="M{x - 5} {y}a5 5 0 1 0 10 0a5 5 0 1 0 -10 0" />{/each}
						<path class="ctr" d="M84 92H144M114 62V122" />
						<g class="dim">
							<path d="M34 40V26M194 40V26M198 44H214M198 140H214" />
							<path class="arr" d="M34 31H194" />
							<path class="arr" d="M209 44V140" />
						</g>
						<path class="hatch" d="M380 110L460 78V118L380 150Z" />
						<path class="ink" pathLength="1" style:--d="0.2" d="M300 78L380 46L460 78L380 110Z" />
						<path class="ink" pathLength="1" style:--d="0.35" d="M300 78V118L380 150L460 118V78M380 110V150" />
						<path class="ink" pathLength="1" style:--d="0.5" d="M356 78a24 11 0 1 0 48 0a24 11 0 1 0 -48 0" />
						<path class="ink" pathLength="1" style:--d="0.55" d="M364 78a16 7.3 0 1 0 32 0a16 7.3 0 1 0 -32 0" />
						{#each [[328, 78], [380, 57], [432, 78], [380, 99]] as [x, y], i (x + "-" + y)}<path class="ink" pathLength="1" style:--d={0.6 + i * 0.04} d="M{x - 5} {y}a5 2.4 0 1 0 10 0a5 2.4 0 1 0 -10 0" />{/each}
						<rect class="scanbar" x="8" y="22" width="2" height="140" />
						{#each CHECK_FLAGS as f, i (i)}
							<path class="link" pathLength="1" d={f.link} style:--i={i} />
							<g class="flag" style:--i={i}>
								<circle class="ring" cx={f.a[0]} cy={f.a[1]} r={f.r} />
								<circle class="badge" cx={f.a[0] + f.r * 0.75 + 4} cy={f.a[1] - f.r * 0.75 - 4} r="6.5" />
								<text x={f.a[0] + f.r * 0.75 + 4} y={f.a[1] - f.r * 0.75 - 1.5}>{i + 1}</text>
							</g>
							{#if i === 1}
								<!-- The bore, X-rayed: its cylinder shows through the block, hidden edges dashed, rim ringed. -->
								<g class="flag" style:--i={i}>
									<path class="xr-fill" d="M356 78V118A24 11 0 0 0 404 118V78A24 11 0 0 1 356 78Z" />
									<path class="xr-hidden" d="M356 78V118M404 78V118M356 118a24 11 0 1 0 48 0a24 11 0 1 0 -48 0" />
									<path class="xr-rim" d="M356 78a24 11 0 1 0 48 0a24 11 0 1 0 -48 0" />
								</g>
							{:else}
								<g class="flag" style:--i={i}><circle class="ring" cx={f.b[0]} cy={f.b[1]} r={f.r * 0.75} /></g>
							{/if}
						{/each}
					</svg>
					<span class="pills">
						{#each CHECK_FLAGS as f, i (i)}<span style:--i={i}><Crosshair size={11} strokeWidth={2.25} /><b>{i + 1}</b><span class="pl">{f.label}</span><span class="ps">{f.short}</span></span>{/each}
					</span>
				</div>
				<span class="label red">Check accuracy</span>
				<p class="metric"><strong>≥95%</strong> <small>precision</small> <strong>≥90%</strong> <small>recall</small></p>
				<h3>Drawing issues caught before they reach the shop floor</h3>
				<p>The agent checks <b>every 2D drawing against its 3D model</b> and flags missing or conflicting dimensions, tolerances and datums, measured on clear-cut checks.</p>
				<p class="who">For a global wind turbine OEM <span class="chip">Quality Agent</span></p>
			</li>
		</ul>
	</div>
{/snippet}

<div class="ens-light">
<Hero
	title="Turn decades of know-how into your unfair advantage"
	accent="unfair advantage"
	eyebrow="Pre6 Enterprise · Custom AI agents"
	cta={{ label: "Talk to us", href: "#get-in-touch" }}
>
	{#snippet ledeContent()}<RotatingLede />{/snippet}
	<HeroStory />
</Hero>

<!-- Where Pre6 Enterprise goes further than the Pre6 Apps, shown on one customer ECN. -->
<section class="block" aria-labelledby="tools-title">
	<div class="sheet" data-drafting="tools">
		<div class="head">
			<SectionHeading id="tools-title" title="Where Pre6 Enterprise goes further">
				Pre6 Apps put one agent in your team's hands. Pre6 Enterprise builds <Highlight>a crew of agents on your history</Highlight> and runs them inside your systems.
			</SectionHeading>
		</div>
		<div class="scale" aria-hidden="true"></div>
		<SpeedStrip />
		<CompareEcn />
		{@render couldHave()}
	</div>
</section>

<section class="block">{@render results()}</section>

<div id="agents" class="story-anchor"><SectorStory /></div>

<section class="block" aria-labelledby="deploy-title" id="deploy">
	<div class="sheet" data-drafting="deploy">
		<div class="head">
			<SectionHeading id="deploy-title" title="Your agents run where your data lives">
				Three ways to run your agents, from fully managed to fully air-gapped.
			</SectionHeading>
		</div>
		<div class="scale" aria-hidden="true"></div>
		<ul class="zones cols-3">
			{#each deployments as d (d.title)}
				<li class="zone">
					<span class="icon"><d.icon size={20} strokeWidth={1.25} /></span>
					<h3>{d.title}</h3>
					<p><b>{d.lead}</b> {d.body}</p>
				</li>
			{/each}
		</ul>
		<div class="could">
			<span class="label">Plugged into the systems you already use</span>
			<ul class="chips">
				{#each systems as sys (sys)}<li>{sys}</li>{/each}
			</ul>
		</div>
	</div>
</section>

<Security />

<section class="block" aria-labelledby="contact-title" id="get-in-touch">
	<div class="sheet contact" data-drafting="contact">
		<div class="contact-copy">
			<SectionHeading id="contact-title" title="Let's put your know-how to work">
				Tell us your biggest bottleneck, and we'll walk you through the agents we'd build for it.
			</SectionHeading>
			<div class="reach">
				<a href="mailto:{EMAIL}"><Mail size={16} strokeWidth={2} />{EMAIL}</a>
			</div>
		</div>
		<div class="contact-form">
			{#if formState === "success"}
				<div class="done">
					<p class="done-title">Thanks, we've got your details.</p>
					<p>Our team will reach out to you shortly.</p>
				</div>
			{:else}
				<form
					novalidate
					onsubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<label>
						<span>Work email</span>
						<input type="email" bind:value={email} onblur={() => (touched.email = true)} placeholder="you@company.com" autocomplete="email" maxlength={MAX_EMAIL_LENGTH} aria-invalid={touched.email && !!issues.email} disabled={formState === "submitting"} />
						{#if touched.email && issues.email}<em>{issues.email}</em>{/if}
					</label>
					<label>
						<span>Company</span>
						<input type="text" bind:value={company} onblur={() => (touched.company = true)} placeholder="Acme Manufacturing" autocomplete="organization" maxlength={MAX_COMPANY_LENGTH} aria-invalid={touched.company && !!issues.company} disabled={formState === "submitting"} />
						{#if touched.company && issues.company}<em>{issues.company}</em>{/if}
					</label>
					<div class="field">
						<label for="ens-phone"><span>Phone</span></label>
						<div class="phone">
							<select bind:value={country} aria-label="Country dialling code" disabled={formState === "submitting"}>
								{#each COUNTRIES as c (c.iso2)}<option value={c}>{c.flag} {c.dial}</option>{/each}
							</select>
							<input id="ens-phone" type="tel" value={phone} oninput={handlePhoneInput} onblur={() => (touched.phone = true)} placeholder="98765 43210" autocomplete="tel-national" maxlength={12} aria-invalid={touched.phone && !!issues.phone} disabled={formState === "submitting"} />
						</div>
						{#if touched.phone && issues.phone}<em>{issues.phone}</em>{/if}
					</div>
					{#if formState === "error"}<p class="err">Something went wrong. Please try again, or email {EMAIL}.</p>{/if}
					<Button type="submit" size="lg" class="submit" disabled={formState === "submitting"}>
						{formState === "submitting" ? "Sending…" : "Talk to us"}
						<ArrowRight size={16} strokeWidth={2.5} />
					</Button>
				</form>
			{/if}
		</div>
	</div>
</section>
</div>

<style>
	/*
	 * Always light, like the home page: the page paints its own white canvas and opts out of the
	 * browser's dark scheme, so form controls and the canvas never turn dark.
	 */
	:global(html:has(.ens-light)) {
		background: #fff;
		color-scheme: light;
	}

	/*
	 * One lightness for every accent: each hue sits at the brand red's lightness (L 0.54 in OKLCH,
	 * from the home palette), so no color shouts louder than another. At that lightness each one
	 * reads at 4.7:1 or better against white, as text or behind white text. Neutrals (ink, grays,
	 * paper) keep their own lightness. Tints are mixed from these, so they follow.
	 */
	:global(.ens-light) {
		--agent-pre6: var(--primary);
		--agent-rfq: oklch(0.54 0.19 264);
		--agent-prod: oklch(0.54 0.15 40);
		--agent-qa: oklch(0.54 0.094 185);
		--ok: oklch(0.54 0.148 150);
		--warn: oklch(0.54 0.116 70);
		/* The blue used for text accents (highlights, notes, chips) is the RFQ Agent's blue. */
		--blue-700: var(--agent-rfq);
		--blue-800: var(--agent-rfq);
	}

	/* The drafting paper and backdrop sit behind the page (z-index -1). The published lens-ui styles
	   give body a white background, which paints over them, so this page lets the html background
	   show through instead. */
	:global(body:has(.ens-light)) {
		background: transparent;
	}

	/* This page keeps the drafting paper but not the gear drawing on the right edge. */
	:global(html:has(.ens-light) .drafting .gear) {
		display: none;
	}

	/* More paper than the home page: the grid reaches further in from both edges and reads a touch
	   stronger, on the first screen and below it. Card backgrounds still keep it out from under the copy. */
	:global(html:has(.ens-light) .drafting .paper.fold) {
		--grid-line: oklch(from var(--muted-foreground) l c h / 0.11);
		--grid-dot: oklch(from var(--muted-foreground) l c h / 0.17);
		--across: linear-gradient(
			90deg,
			#000 var(--edge),
			transparent calc(var(--edge) + 560px),
			transparent calc(100% - var(--edge) - 560px),
			#000 calc(100% - var(--edge))
		);
	}

	:global(html:has(.ens-light) .drafting .paper.rest) {
		--grid-line: oklch(from var(--muted-foreground) l c h / 0.15);
		--grid-dot: oklch(from var(--muted-foreground) l c h / 0.22);
		--across: linear-gradient(90deg, #000 10%, transparent 36%, transparent 64%, #000 90%);
	}

	/* Section column and sheet, matching the home page's Security section. */
	/* Anchor links (#get-in-touch, #deploy...) land below the docked navbar. */
	.block,
	.proof,
	.story-anchor {
		scroll-margin-top: calc(var(--nav-h) + 16px);
	}

	/*
	 * Agents you could have: two rows of agent cards drifting in opposite directions, fading at both
	 * edges, paused under the pointer; the reader's own agent waits at the end, dashed, as the way in.
	 */
	.could {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 26px var(--gutter) 30px;
		border-top: 1px solid var(--border);
	}

	.could-body {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		align-items: center;
		gap: 18px;
	}

	.lanes {
		display: flex;
		flex-direction: column;
		gap: 10px;
		min-width: 0;
		margin-block: -6px -12px;
		padding-block: 6px 12px;
		overflow: hidden;
		mask-image: linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent);
	}

	.lane {
		display: flex;
		width: max-content;
		animation: drift 46s linear infinite;
	}

	.lanes:hover .lane {
		animation-play-state: paused;
	}

	@keyframes drift {
		to {
			transform: translateX(-50%);
		}
	}

	.lane-run {
		display: flex;
		gap: 10px;
		padding-right: 10px;
	}

	.ag-card {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px 14px 8px 8px;
		border-radius: 11px;
		background: #fff;
		box-shadow:
			inset 0 0 0 1px var(--border),
			0 6px 16px -12px oklch(0.2 0.05 264 / 0.35);
		white-space: nowrap;
		transition:
			transform 0.3s var(--ease),
			box-shadow 0.3s var(--ease);
	}

	.ag-card:hover {
		transform: translateY(-2px);
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--c) 45%, transparent),
			0 12px 22px -14px var(--c);
	}

	.ag-ic {
		display: grid;
		flex: none;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--c);
		color: #fff;
		box-shadow: 0 4px 10px -5px var(--c);
	}

	.ag-t {
		display: flex;
		flex-direction: column;
		gap: 1px;
	}

	.ag-t b {
		color: var(--ink);
		font-size: 14px;
		font-weight: 600;
		line-height: 1.2;
	}

	/* Plain chips: the systems the agents plug into. */
	.chips {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.chips li {
		padding: 6px 12px;
		border: 1px solid var(--border);
		background: #fff;
		color: var(--ink);
		font-size: 14px;
		font-weight: 500;
	}

	/* The reader's own agent: dashed in red, the way into the contact form. */
	.yours {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 14px 18px 14px 14px;
		border: 1.5px dashed var(--primary);
		border-radius: 14px;
		background:
			radial-gradient(120% 120% at 0% 0%, color-mix(in oklch, var(--primary) 10%, transparent), transparent 60%),
			#fff;
		color: var(--primary);
		text-decoration: none;
		transition:
			transform 0.3s var(--ease),
			box-shadow 0.3s var(--ease);
	}

	.yours:hover {
		transform: translateY(-2px);
		box-shadow: 0 14px 28px -18px var(--primary);
	}

	.yours-ic {
		display: grid;
		flex: none;
		place-items: center;
		width: 38px;
		height: 38px;
		border-radius: 10px;
		background: var(--primary);
		color: #fff;
		box-shadow: 0 0 0 4px color-mix(in oklch, var(--primary) 15%, transparent);
	}

	.yours-t {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.yours-t b {
		color: var(--ink);
		font-size: 16px;
		font-weight: 700;
	}

	@media (max-width: 767px) {
		.could {
			padding-inline: 20px;
		}

		.could-body {
			grid-template-columns: minmax(0, 1fr);
		}
	}

	/* Still, the cards simply wrap. */
	@media (prefers-reduced-motion: reduce) {
		.lanes {
			mask-image: none;
		}

		.lane {
			width: auto;
			animation: none;
		}

		.lane-run[aria-hidden] {
			display: none;
		}

		.lane-run {
			flex-wrap: wrap;
		}
	}

	.block {
		width: 100%;
		max-width: var(--page-max);
		margin: 0 auto;
		padding: 0 32px 72px;
		color: var(--foreground);
	}

	.sheet {
		--gutter: 44px;
		--tick: color-mix(in oklch, var(--muted-foreground) 55%, transparent);
		--tick-faint: color-mix(in oklch, var(--muted-foreground) 30%, transparent);
		overflow: hidden;
		border: 1px solid var(--border);
		border-radius: 8px;
		background: var(--surface);
	}

	.head {
		padding: 44px var(--gutter) 36px;
	}

	.scale {
		height: 18px;
		border-top: 1px solid var(--border);
		background-image:
			linear-gradient(90deg, var(--tick) 1px, transparent 1px),
			linear-gradient(90deg, var(--tick) 1px, transparent 1px),
			linear-gradient(90deg, var(--tick-faint) 1px, transparent 1px);
		background-size: 80px 13px, 40px 8px, 8px 5px;
		background-position: var(--gutter) 100%, var(--gutter) 100%, var(--gutter) 100%;
		background-repeat: repeat-x;
	}

	.zones {
		display: grid;
		border-top: 1px solid var(--border);
	}

	.cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	.cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }

	.zone {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
		padding: 28px 32px 32px;
	}

	.zone:first-child {
		padding-left: var(--gutter);
	}

	.zone + .zone {
		border-left: 1px solid var(--border);
	}

	/* A registration cross where each divider meets the ruler. */
	.zone + .zone::before {
		content: "";
		position: absolute;
		top: -6px;
		left: -6px;
		width: 11px;
		height: 11px;
		background:
			linear-gradient(var(--tick), var(--tick)) center / 1px 100% no-repeat,
			linear-gradient(var(--tick), var(--tick)) center / 100% 1px no-repeat;
	}

	.icon {
		display: grid;
		place-items: center;
		width: 48px;
		height: 48px;
		margin-bottom: 12px;
		border: 1px solid var(--border);
		background: #fff;
		color: var(--ink-soft);
	}

	h3 {
		color: var(--ink);
		font-family: "Inter Variable", var(--font-sans);
		font-variation-settings: "opsz" 32;
		font-size: 20px;
		line-height: 1.25;
		font-weight: 700;
		letter-spacing: -0.01em;
		text-wrap: balance;
	}

	.zone p {
		max-width: 30rem;
		color: var(--muted-foreground);
		font-size: 15.5px;
		line-height: 24px;
		text-wrap: pretty;
	}

	/* Key phrases in body copy: ink and semibold against the muted sentence. */
	.zone p b {
		color: var(--ink);
		font-weight: 600;
	}

	.label {
		color: var(--muted-foreground);
		font-family: var(--font-mono);
		font-size: 11px;
		line-height: 16px;
		font-weight: 500;
		letter-spacing: 0.06em;
		text-transform: uppercase;
	}

	.label.red {
		color: var(--primary);
	}

	/* Results sheet. */
	.proof {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		--pop: cubic-bezier(0.34, 1.56, 0.64, 1);
	}

	/*
	 * A graphic over each result, drawn like the sector sheets: ink line work on grid paper, with what
	 * the agent adds in its color (`--ag`). It plays once when the sheet scrolls into view.
	 */
	.viz {
		--ink-line: oklch(0.32 0.02 262);
		position: relative;
		align-self: stretch;
		height: 188px;
		margin-bottom: 14px;
		border: 1px solid var(--border);
		border-radius: 8px;
		background-color: #fff;
		background-image:
			linear-gradient(color-mix(in oklch, var(--blue-300) 14%, transparent) 1px, transparent 1px),
			linear-gradient(90deg, color-mix(in oklch, var(--blue-300) 14%, transparent) 1px, transparent 1px);
		background-size: 20px 20px;
		background-position: -1px -1px;
	}

	.v-k {
		position: absolute;
		top: 12px;
		left: 16px;
		color: var(--muted-foreground);
		font: 500 10.5px/1 var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	/* The agent behind the result, in the hero's avatar style. */
	.v-agent {
		position: absolute;
		top: 8px;
		right: 14px;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		color: var(--ink);
		font-size: 11.5px;
		font-weight: 600;
	}

	.v-av {
		display: grid;
		place-items: center;
		width: 18px;
		height: 18px;
		border-radius: 5px;
		background: var(--ag);
		color: #fff;
	}

	/* Turnaround: two plain bars on one scale, 504 hours against 24, drawn in once. */
	.turn {
		display: flex;
		align-items: center;
		padding: 34px 20px 14px 16px;
	}

	.bars {
		display: grid;
		grid-template-columns: auto minmax(0, 1fr) auto;
		align-items: center;
		gap: 14px 14px;
		width: 100%;
	}

	.b-k {
		color: var(--muted-foreground);
		font: 500 11px/1 var(--font-mono);
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.b-k.strong {
		color: var(--ink);
		font-weight: 700;
	}

	.b-track {
		position: relative;
		height: 12px;
		overflow: hidden;
		border-radius: 999px;
		background: color-mix(in oklch, var(--muted-foreground) 9%, #fff);
	}

	.b-track i {
		position: absolute;
		inset: 0 auto 0 0;
		border-radius: inherit;
		transform-origin: left;
	}

	.b-slow {
		width: 100%;
		background: repeating-linear-gradient(-45deg, color-mix(in oklch, var(--muted-foreground) 55%, #fff) 0 4px, color-mix(in oklch, var(--muted-foreground) 30%, #fff) 4px 8px);
	}

	/* 24 of 504 hours, never so thin it disappears. */
	.b-fast {
		width: max(12px, calc(100% * 24 / 504));
		background: var(--primary);
	}

	.b-v {
		color: var(--muted-foreground);
		font: 700 13px/1 var(--font-mono);
		font-variant-numeric: tabular-nums;
		text-align: right;
	}

	.b-v.fast {
		color: var(--primary);
	}

	/* The hours won back, as a solid pill in the agent's blue under the bars. */
	.gain {
		grid-column: 1 / -1;
		justify-self: end;
		padding: 4px 11px;
		border-radius: 999px;
		background: var(--ag);
		box-shadow: 0 6px 14px -8px var(--ag);
		color: #fff;
		font: 500 11.5px/1.3 var(--font-mono);
		white-space: nowrap;
	}

	.gain b {
		font-weight: 700;
	}

	.proof .gain {
		opacity: 0;
		translate: 0 4px;
	}

	.proof:global([data-seen]) .gain {
		opacity: 1;
		translate: none;
		transition:
			opacity 0.4s ease 1.2s,
			translate 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.2s;
	}

	.proof .b-track i {
		transform: scaleX(0);
	}

	.proof:global([data-seen]) .b-slow {
		transform: none;
		transition: transform 1.4s cubic-bezier(0.4, 0, 0.6, 1) 0.2s;
	}

	.proof:global([data-seen]) .b-fast {
		transform: none;
		transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
	}

	/* Drawing check: ink line work that plots itself, the agent's rings and links in teal. */
	.check svg {
		position: absolute;
		top: 30px;
		left: 12px;
		width: calc(100% - 24px);
		height: calc(100% - 70px);
		overflow: visible;
	}

	.arrowhead {
		fill: var(--ink-line);
	}

	.hatchline {
		stroke: var(--ink-line);
		stroke-width: 0.6;
	}

	.paper {
		fill: #fff;
		stroke: var(--border);
	}

	.thin {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.75;
	}

	.ink {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 1.4;
		stroke-linecap: round;
		stroke-linejoin: round;
		stroke-dasharray: 1;
		stroke-dashoffset: 1;
	}

	.proof:global([data-seen]) .ink {
		animation: draw 1.1s cubic-bezier(0.45, 0, 0.2, 1) both;
		animation-delay: calc(var(--d, 0) * 1s);
	}

	@keyframes draw {
		from {
			stroke-dashoffset: 1;
		}
		to {
			stroke-dashoffset: 0;
		}
	}

	.ctr {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.6;
		stroke-dasharray: 8 3 2 3;
		opacity: 0.5;
	}

	.dim {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.7;
		opacity: 0;
		transition: opacity 0.5s var(--ease) 0.9s;
	}

	.dim .arr {
		marker-start: url(#rv-arr);
		marker-end: url(#rv-arr);
	}

	.hatch {
		fill: url(#rv-hatch);
		opacity: 0;
		transition: opacity 0.6s var(--ease) 0.8s;
	}

	.proof:global([data-seen]) .dim {
		opacity: 0.8;
	}

	.proof:global([data-seen]) .hatch {
		opacity: 0.55;
	}

	.scanbar {
		fill: var(--ag);
		opacity: 0;
	}

	.proof:global([data-seen]) .scanbar {
		animation: sweep 1.4s var(--ease) 0.6s both;
	}

	@keyframes sweep {
		0% { opacity: 0.9; transform: translateX(0); }
		90% { opacity: 0.9; }
		100% { opacity: 0; transform: translateX(222px); }
	}

	.ring {
		fill: color-mix(in oklch, var(--ag) 7%, transparent);
		stroke: var(--ag);
		stroke-width: 2;
	}

	.badge {
		fill: var(--ag);
	}

	/* The X-rayed bore on the model. */
	.xr-fill {
		fill: color-mix(in oklch, var(--ag) 14%, transparent);
	}

	.xr-hidden {
		fill: none;
		stroke: var(--ag);
		stroke-width: 1.2;
		stroke-dasharray: 3 2.5;
	}

	.xr-rim {
		fill: color-mix(in oklch, var(--ag) 8%, transparent);
		stroke: var(--ag);
		stroke-width: 2;
	}

	.flag text {
		fill: #fff;
		font: 700 8px var(--font-mono);
		text-anchor: middle;
	}

	.flag {
		opacity: 0;
		transform-box: fill-box;
		transform-origin: center;
	}

	/* Each flag lands with a squash and a small rebound. */
	.proof:global([data-seen]) .flag {
		animation: flag-land 0.5s var(--ease) both;
		animation-delay: calc(1.2s + var(--i) * 0.35s);
	}

	@keyframes flag-land {
		0% {
			opacity: 0;
			transform: scale(0.4);
		}
		45% {
			opacity: 1;
			transform: scale(1.2, 0.85);
		}
		70% {
			opacity: 1;
			transform: scale(0.94, 1.06);
		}
		100% {
			opacity: 1;
			transform: none;
		}
	}

	.link {
		fill: none;
		stroke: var(--ag);
		stroke-width: 1.3;
		stroke-dasharray: 1;
		stroke-dashoffset: 1;
		transition: stroke-dashoffset 0.7s var(--ease);
		transition-delay: calc(1.4s + var(--i) * 0.35s);
	}

	.proof:global([data-seen]) .link {
		stroke-dashoffset: 0;
	}

	/* The issues, as the hero's attachment chips. */
	.pills {
		position: absolute;
		right: 12px;
		bottom: 10px;
		left: 12px;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 6px;
	}

	.pills > span {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 3px 8px;
		border-radius: 7px;
		background: color-mix(in oklch, var(--ag) 6%, #fff);
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--ag) 30%, transparent);
		color: var(--ag);
		font-size: 11.5px;
		font-weight: 500;
		opacity: 0;
		transform: translateY(4px);
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop);
		transition-delay: calc(1.6s + var(--i) * 0.35s);
	}

	.pills b {
		font: 600 11px var(--font-mono);
	}

	.pills .pl,
	.pills .ps {
		padding: 0;
		background: none;
		box-shadow: none;
		color: inherit;
		font: inherit;
		opacity: 1;
		transform: none;
		transition: none;
	}

	.pills .ps {
		display: none;
	}

	.proof:global([data-seen]) .pills > span {
		opacity: 1;
		transform: none;
	}

	@media (max-width: 639px) {
		.viz {
			height: 172px;
		}

		.turn {
			padding-inline: 14px;
		}

		/* Labels over their bars, so the bars keep the card's width. */
		.bars {
			grid-template-columns: minmax(0, 1fr) auto;
			row-gap: 6px;
		}

		.b-k {
			grid-column: 1 / -1;
		}

		.b-k.strong {
			margin-top: 6px;
		}

		.gain {
			justify-self: start;
			max-width: 100%;
			margin-top: 2px;
			white-space: normal;
		}

		.v-agent {
			display: none;
		}

		.pills {
			gap: 4px;
		}

		.pills > span {
			padding: 2px 6px;
			font-size: 10.5px;
		}

		.pills > span :global(svg) {
			display: none;
		}

		/* Short labels on phones, so the three issues fit on one row under the drawing. */
		.pills .pl {
			display: none;
		}

		.pills .ps {
			display: inline;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.viz *,
		.viz *::before,
		.viz *::after {
			transition: none !important;
			animation: none !important;
		}

		.viz .ink {
			stroke-dashoffset: 0;
		}

		.viz .flag {
			opacity: 1;
		}
	}

	.metric {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 6px 14px;
		margin: 2px 0 4px;
		color: var(--ink);
		font-family: "Inter Variable", var(--font-sans);
	}

	.zone .metric {
		color: var(--ink);
	}

	.metric s {
		color: var(--muted-foreground);
		font-size: 26px;
		font-weight: 500;
	}

	.metric strong {
		color: var(--primary);
		font-size: 44px;
		line-height: 1;
		font-weight: 700;
		letter-spacing: -0.03em;
	}

	.metric small {
		color: var(--muted-foreground);
		font-size: 15px;
		font-weight: 600;
		margin-right: 10px;
	}

	.zone .who {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 12px;
		margin-top: auto;
		padding-top: 14px;
		border-top: 1px solid var(--border);
		color: var(--ink);
		font-weight: 600;
		font-size: 14.5px;
		max-width: none;
		width: 100%;
	}

	.chip {
		padding: 1px 8px;
		border: 1px solid var(--blue-200);
		background: var(--blue-50);
		color: var(--blue-800);
		font-family: var(--font-mono);
		font-size: 11.5px;
		font-weight: 500;
	}

	/* Contact. */
	.contact {
		display: grid;
		grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
	}

	.contact-copy {
		display: flex;
		flex-direction: column;
		gap: 28px;
		padding: 44px var(--gutter);
	}

	.reach {
		display: flex;
		flex-wrap: wrap;
		gap: 10px 24px;
	}

	.reach a {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		color: var(--ink);
		font-weight: 600;
		font-size: 15px;
	}

	.reach a :global(svg) {
		color: var(--primary);
	}

	.contact-form {
		padding: 36px var(--gutter);
		border-left: 1px solid var(--border);
		background: #fff;
	}

	form {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	label,
	.field {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	label span {
		color: var(--ink);
		font-size: 14px;
		font-weight: 500;
	}

	input,
	select {
		height: 44px;
		padding: 0 12px;
		border: 1px solid var(--border);
		border-radius: 6px;
		background: #fff;
		color: var(--foreground);
		font-size: 15px;
	}

	input:focus-visible,
	select:focus-visible {
		outline: 2px solid color-mix(in oklch, var(--primary) 35%, transparent);
		outline-offset: 0;
		border-color: var(--primary);
	}

	input[aria-invalid="true"] {
		border-color: var(--primary);
	}

	.phone {
		display: flex;
	}

	.phone select {
		flex: 0 0 104px;
		border-right: 0;
		border-radius: 6px 0 0 6px;
		background: var(--surface);
	}

	.phone input {
		flex: 1;
		min-width: 0;
		border-radius: 0 6px 6px 0;
	}

	em,
	.err {
		color: var(--primary);
		font-size: 13px;
		font-style: normal;
	}

	form :global(.submit) {
		height: 44px;
		margin-top: 4px;
		font-size: 15px;
		font-weight: 600;
	}

	.done {
		display: flex;
		flex-direction: column;
		gap: 6px;
		color: var(--muted-foreground);
	}

	.done-title {
		color: var(--ink);
		font-size: 18px;
		font-weight: 600;
	}

	@media (max-width: 1023px) {
		.cols-2,
		.cols-3,
		.contact {
			grid-template-columns: minmax(0, 1fr);
		}

		.cols-2 .zone,
		.cols-3 .zone {
			padding-left: var(--gutter);
		}

		.cols-2 .zone + .zone,
		.cols-3 .zone + .zone {
			border-left: 0;
			border-top: 1px solid var(--border);
		}

		.cols-2 .zone + .zone::before,
		.cols-3 .zone + .zone::before {
			display: none;
		}

		.contact-form {
			border-left: 0;
			border-top: 1px solid var(--border);
		}
	}

	@media (max-width: 767px) {
		.block {
			padding: 0 16px 48px;
		}

		.sheet {
			--gutter: 20px;
		}

		.head,
		.contact-copy {
			padding-block: 32px 28px;
		}

		.metric strong {
			font-size: 36px;
		}
	}
</style>
